const RAW_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();
const API_BASE_URL = RAW_BASE_URL.replace(/\/$/, '');

const buildUrl = (path = '') => {
  if (!API_BASE_URL) {
    return path;
  }

  try {
    const url = new URL(path, API_BASE_URL);
    return url.toString();
  } catch (error) {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${API_BASE_URL}${normalizedPath}`;
  }
};

const BUSINESS_HELP_ENDPOINTS = [
  '/api/public/content/business-help-topics',
  '/api/admin/content/public/business-help-topics'
];

const normalizeTopicsResponse = (data) => {
  const topics = Array.isArray(data?.topics) ? data.topics : [];

  return topics.map((topic) => {
    if (!topic?.photo?.url) {
      return topic;
    }

    const photoUrl = topic.photo.url.startsWith('http') ? topic.photo.url : buildUrl(topic.photo.url);

    return {
      ...topic,
      photo: {
        ...topic.photo,
        url: photoUrl
      }
    };
  });
};

export const fetchBusinessHelpTopics = async () => {
  let lastError = null;

  for (const endpoint of BUSINESS_HELP_ENDPOINTS) {
    const url = buildUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        const error = new Error(`Failed to load Business Help Topics: ${response.status} ${errorText}`.trim());
        error.status = response.status;
        error.urlTried = url;

        if (response.status === 404) {
          lastError = error;
          continue;
        }

        throw error;
      }

      const data = await response.json();
      return normalizeTopicsResponse(data);
    } catch (error) {
      lastError = error;

      if (error?.status !== 404) {
        break;
      }
    }
  }

  throw lastError || new Error('Failed to load Business Help Topics');
};

const PRIMARY_BUSINESS_ENDPOINTS = [
  '/api/public/content/primary-businesses',
  '/api/admin/content/public/primary-businesses'
];

const SECOND_BUSINESS_ENDPOINTS = PRIMARY_BUSINESS_ENDPOINTS;

const normalizeBusinessOptionsResponse = (data) => {
  const items = Array.isArray(data?.items) ? data.items : [];

  return items.map((item) => {
    if (!item?.url) {
      return item;
    }

    const itemUrl = item.url.startsWith('http') ? item.url : buildUrl(item.url);

    return {
      ...item,
      url: itemUrl
    };
  });
};

const fetchBusinessOptions = async (endpoints, label) => {
  let lastError = null;

  for (const endpoint of endpoints) {
    const url = buildUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'GET'
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        const error = new Error(`Failed to load ${label}: ${response.status} ${errorText}`.trim());
        error.status = response.status;
        error.urlTried = url;

        if (response.status === 404) {
          lastError = error;
          continue;
        }

        throw error;
      }

      const data = await response.json();
      return normalizeBusinessOptionsResponse(data);
    } catch (error) {
      lastError = error;

      if (error?.status !== 404) {
        break;
      }
    }
  }

  throw lastError || new Error(`Failed to load ${label}`);
};

export const fetchPrimaryBusinessOptions = async () =>
  fetchBusinessOptions(PRIMARY_BUSINESS_ENDPOINTS, 'Primary Business Options');

export const fetchSecondBusinessOptions = async () =>
  fetchBusinessOptions(SECOND_BUSINESS_ENDPOINTS, 'Second Business Options');

export default {
  fetchBusinessHelpTopics,
  fetchSecondBusinessOptions,
  fetchPrimaryBusinessOptions
};
